/**
 * Typed fetch-Wrapper gegen das Backend (ADR-003 v2: SWA Built-in Auth / Entra).
 *  - AuthN macht die SWA-Plattform; Auth-Status via /.auth/me (First-Party-Cookie).
 *  - AuthZ macht das Backend (E-Mail-Allow-List): 401 = nicht angemeldet,
 *    403 = angemeldet aber nicht berechtigt.
 *  - login()/logout() sind SSO-Redirects (/.auth/login/aad bzw. /.auth/logout).
 *
 * Dev-Mock: Mit VITE_USE_FIXTURE=1 werden Endpunkte aus der synthetischen Fixture
 * bedient und /.auth/me mockt einen angemeldeten Nutzer (kein Backend/AAD nötig).
 */
import { ApiError, ForbiddenError, UnauthorizedError } from './errors';
import { assetFromFixture, metaFromFixture, portfolioFromFixture } from './fixture';
import type { Entity, Meta, Portfolio, SnapshotFixture } from '../types/snapshot';

const USE_FIXTURE = import.meta.env.VITE_USE_FIXTURE === '1';

/** SWA-Principal aus /.auth/me. */
export interface ClientPrincipal {
  identityProvider: string;
  userId: string;
  userDetails: string;
  userRoles: string[];
}

async function loadFixture(): Promise<SnapshotFixture> {
  const mod = await import('../mock/sample_dashboard_data.example.json');
  return mod.default as unknown as SnapshotFixture;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    ...init,
  });

  if (res.status === 401) throw new UnauthorizedError();
  if (res.status === 403) throw new ForbiddenError();
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      detail = body?.message ?? detail;
    } catch {
      /* kein JSON-Body */
    }
    throw new ApiError(res.status, detail);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

function ssoLoginUrl(): string {
  const redirect = encodeURIComponent(
    window.location.pathname + window.location.search,
  );
  return `/.auth/login/aad?post_login_redirect_uri=${redirect}`;
}

export const api = {
  useFixture: USE_FIXTURE,

  /** Auth-Status: /.auth/me → ClientPrincipal oder null. */
  async getAuthMe(): Promise<ClientPrincipal | null> {
    if (USE_FIXTURE) {
      return {
        identityProvider: 'fixture',
        userId: 'dev',
        userDetails: 'dev@fixture.local',
        userRoles: ['anonymous', 'authenticated', 'viewer'],
      };
    }
    const res = await fetch('/.auth/me', { credentials: 'include' });
    if (!res.ok) return null;
    const body = (await res.json()) as { clientPrincipal: ClientPrincipal | null };
    return body?.clientPrincipal ?? null;
  },

  /** SSO-Login: Redirect zu Entra (nach Login zurück auf die aktuelle Seite). */
  login(): void {
    if (USE_FIXTURE) return; // Dev: bereits „angemeldet" (getAuthMe mockt Principal)
    window.location.assign(ssoLoginUrl());
  },

  /** SSO-Logout: Redirect zu /.auth/logout. */
  logout(): void {
    if (USE_FIXTURE) return;
    window.location.assign('/.auth/logout?post_logout_redirect_uri=/login');
  },

  async getMeta(): Promise<Meta> {
    if (USE_FIXTURE) return metaFromFixture(await loadFixture());
    return request<Meta>('/api/meta');
  },

  async getPortfolio(): Promise<Portfolio> {
    if (USE_FIXTURE) return portfolioFromFixture();
    return request<Portfolio>('/api/portfolio');
  },

  async getAsset(short: string): Promise<Entity> {
    if (USE_FIXTURE) {
      const e = assetFromFixture(short);
      if (!e) throw new ApiError(404, `Asset „${short}" nicht gefunden`);
      return e;
    }
    return request<Entity>(`/api/asset/${encodeURIComponent(short)}`);
  },
};
